import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { teamService, Team } from "@/services/teamService";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { projectService } from "@/services/projectService";
import { useProjectSelection } from "@/contexts/ProjectSelectionContext";

const COUNTRIES = [
  { value: "CA", label: "Canada" },
  { value: "US", label: "United States" },
];

const PROVINCES_CA = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
];

const STATES_US = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

export default function Settings() {
  const { user, changePassword, deleteAccount } = useAuth();
  const { isAdmin } = useRoleAccess();
  const { projects, setProjects, refreshProjects, isLoading: isLoadingProjects } = useProjectSelection();
  const [loading, setLoading] = useState(false);
  const [team, setTeam] = useState<Team | null>(null);

  const isOwner = !!(user?.email && team?.adminEmail && user.email.toLowerCase() === team.adminEmail.toLowerCase());
  
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    province: "",
    country: "",
    postalCode: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [newLocationName, setNewLocationName] = useState("");
  const [savingProjectId, setSavingProjectId] = useState<string | null>(null);
  const [isSavingLocation, setIsSavingLocation] = useState(false);
  const [editedNames, setEditedNames] = useState<Record<string, string>>({});

  const regionOptions = profileData.country === "US" ? STATES_US : PROVINCES_CA;

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => a.name.localeCompare(b.name)),
    [projects]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profile, teamData] = await Promise.all([
          teamService.getUserProfile(),
          teamService.getTeam(),
        ]);
        setTeam(teamData);
        const nameParts = (profile.name || "").split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";
        
        setProfileData({
          firstName,
          lastName,
          email: profile.email || "",
          phone: teamData.phone || "",
          address: teamData.address || "",
          city: teamData.city || "",
          province: teamData.province || "",
          country: teamData.country || "",
          postalCode: teamData.postalCode || "",
        });
      } catch (error) {
        console.error("Failed to load profile", error);
        toast.error("Failed to load profile details");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  useEffect(() => {
    setEditedNames({});
  }, [projects]);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fullName = `${profileData.firstName} ${profileData.lastName}`.trim();
      await teamService.updateUserProfile(fullName);
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Failed to update profile", error);
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleBillingDetailsUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await teamService.updateTeam({
        phone: profileData.phone,
        address: profileData.address,
        city: profileData.city,
        province: profileData.province,
        country: profileData.country,
        postalCode: profileData.postalCode,
      });
      toast.success("Billing details updated successfully");
    } catch (error) {
      console.error("Failed to update billing details", error);
      toast.error("Failed to update billing details");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    
    setLoading(true);
    try {
      await changePassword(passwordData.newPassword);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      // Error is handled in changePassword
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirm("Are you sure you want to delete your account? This action cannot be undone.")) {
      setLoading(true);
      try {
        await deleteAccount();
      } catch (error) {
        // Error is handled in deleteAccount
        setLoading(false);
      }
    }
  };

  const handleCreateLocation = async () => {
    if (!isAdmin) {
      toast.error("Permission denied", { description: "Only admins can create locations" });
      return;
    }

    const name = newLocationName.trim();
    if (!name) {
      toast.error("Location name is required");
      return;
    }

    try {
      setIsSavingLocation(true);
      const created = await projectService.create(name);
      setProjects((prev) => [...prev, created]);
      setNewLocationName("");
      toast.success("Location created");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error("Failed to create location", { description: message });
    } finally {
      setIsSavingLocation(false);
    }
  };

  const handleRenameLocation = async (projectId: string, name: string) => {
    if (!isAdmin) {
      toast.error("Permission denied", { description: "Only admins can update locations" });
      return;
    }

    const trimmed = name.trim();
    if (!trimmed) return;

    try {
      setSavingProjectId(projectId);
      const updated = await projectService.update(projectId, trimmed);
      setProjects((prev) => prev.map((project) => (project.id === projectId ? updated : project)));
      setEditedNames((prev) => {
        const { [projectId]: _omit, ...rest } = prev;
        return rest;
      });
      toast.success("Location updated");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error("Failed to update location", { description: message });
    } finally {
      setSavingProjectId(null);
    }
  };

  const handleSuspendLocation = async (projectId: string) => {
    if (!isAdmin) {
      toast.error("Permission denied", { description: "Only admins can suspend locations" });
      return;
    }

    try {
      const updated = await projectService.suspend(projectId);
      setProjects((prev) => prev.map((project) => (project.id === projectId ? updated : project)));
      toast.success("Location suspended");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error("Failed to suspend location", { description: message });
    }
  };

  const handleEnableLocation = async (projectId: string) => {
    if (!isAdmin) {
      toast.error("Permission denied", { description: "Only admins can enable locations" });
      return;
    }

    try {
      const updated = await projectService.enable(projectId);
      setProjects((prev) => prev.map((project) => (project.id === projectId ? updated : project)));
      toast.success("Location enabled");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error("Failed to enable location", { description: message });
    }
  };

  const handleDeleteLocation = async (projectId: string) => {
    if (!isAdmin) {
      toast.error("Permission denied", { description: "Only admins can delete locations" });
      return;
    }

    try {
      await projectService.delete(projectId);
      setProjects((prev) => prev.filter((project) => project.id !== projectId));
      setEditedNames((prev) => {
        const { [projectId]: _omit, ...rest } = prev;
        return rest;
      });
      toast.success("Location deleted");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error("Failed to delete location", { description: message });
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Profile Details */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Details</CardTitle>
          <CardDescription>Update your personal information</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={profileData.firstName}
                  onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={profileData.lastName}
                  onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={profileData.email}
                disabled={true} // Email cannot be changed here usually
                className="bg-muted"
              />
            </div>

            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {isAdmin && (
        <>
        {/* Billing Details */}
        <Card>
          <CardHeader>
            <CardTitle>Billing Details</CardTitle>
            <CardDescription>Address and phone used for billing and Stripe</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleBillingDetailsUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  disabled={loading}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  value={profileData.address}
                  onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                  disabled={loading}
                  placeholder="123 Main St"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={profileData.city}
                    onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                    disabled={loading}
                    placeholder="Toronto"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={profileData.country}
                    onValueChange={(v) => setProfileData({ ...profileData, country: v, province: "" })}
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="province">
                    {profileData.country === "US" ? "State" : "Province"}
                  </Label>
                  <Select
                    value={profileData.province}
                    onValueChange={(v) => setProfileData({ ...profileData, province: v })}
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={profileData.country === "US" ? "Select state" : "Select province"} />
                    </SelectTrigger>
                    <SelectContent>
                      {regionOptions.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postalCode">
                    {profileData.country === "US" ? "Zip Code" : "Postal Code"}
                  </Label>
                  <Input
                    id="postalCode"
                    value={profileData.postalCode}
                    onChange={(e) => setProfileData({ ...profileData, postalCode: e.target.value })}
                    disabled={loading}
                    placeholder={profileData.country === "US" ? "10001" : "M5V 2T6"}
                  />
                </div>
              </div>

              <Button type="submit" disabled={loading}>
                {loading ? "Saving..." : "Save Billing Details"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Locations */}
      <Card>
        <CardHeader>
          <CardTitle>Locations</CardTitle>
          <CardDescription>Manage the locations available for your team</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="new-location">Add a location</Label>
            <div className="flex flex-col sm:flex-row gap-2 max-w-md mt-2">
              <Input
                id="new-location"
                placeholder="Location name"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                disabled={!isAdmin}
              />
              <Button
                onClick={handleCreateLocation}
                disabled={!isAdmin || !newLocationName.trim() || isSavingLocation}
              >
                {isSavingLocation ? "Adding..." : "Add"}
              </Button>
            </div>
            {!isAdmin && (
              <p className="text-xs text-muted-foreground mt-2">Only admins can create locations.</p>
            )}
          </div>

          <div className="space-y-3">
            {isLoadingProjects ? (
              <p className="text-sm text-muted-foreground">Loading locations...</p>
            ) : sortedProjects.length === 0 ? (
              <p className="text-sm text-muted-foreground">No locations yet.</p>
            ) : (
              sortedProjects.map((project) => {
                const draftName = editedNames[project.id] ?? project.name;
                const trimmedDraft = draftName.trim();
                const hasNameChanged = trimmedDraft !== project.name.trim();
                const saveDisabled =
                  !isAdmin || !trimmedDraft || !hasNameChanged || savingProjectId === project.id;
                const status = (project.status || "enabled").toLowerCase();
                const statusBadgeClasses =
                  status === "suspended"
                    ? "bg-red-500/10 text-red-400 border border-red-500/20"
                    : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20";

                return (
                  <div key={project.id} className="rounded-xl border border-border/50 bg-muted/20 p-4 space-y-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <div className="flex-1 space-y-2">
                        <Label
                          htmlFor={`location-name-${project.id}`}
                          className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          Location Name
                        </Label>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <Input
                            id={`location-name-${project.id}`}
                            value={draftName}
                            onChange={(e) => {
                              const value = e.target.value;
                              setEditedNames((prev) => {
                                const next = { ...prev, [project.id]: value };
                                if (value === project.name) {
                                  const { [project.id]: _omit, ...rest } = next;
                                  return rest;
                                }
                                return next;
                              });
                            }}
                            disabled={!isAdmin || savingProjectId === project.id}
                          />
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={saveDisabled}
                              onClick={() => handleRenameLocation(project.id, draftName)}
                            >
                              {savingProjectId === project.id ? "Saving..." : "Save"}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!isAdmin || status === "suspended"}
                              onClick={() => handleSuspendLocation(project.id)}
                            >
                              Disable
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!isAdmin || status === "enabled"}
                              onClick={() => handleEnableLocation(project.id)}
                            >
                              Enable
                            </Button>
                          </div>
                        </div>
                      </div>
                      <Badge variant="outline" className={`h-fit px-3 py-1 text-xs font-medium ${statusBadgeClasses}`}>
                        {status === "suspended" ? "Disabled" : "Enabled"}
                      </Badge>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={!isAdmin}
                        onClick={() => handleDeleteLocation(project.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
      </>
      )}

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            {/* Supabase doesn't require current password for update if session is valid, 
                but UI asks for it. We can ignore it or verify it if we had an endpoint.
                For now we only use new password. */}
            
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                disabled={loading}
              />
            </div>

            <Button type="submit" disabled={loading}>
              {loading ? "Changing..." : "Change Password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Account Actions - Owner only */}
      {isOwner && (
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
            <CardDescription>Irreversible account actions</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" onClick={handleDeleteAccount} disabled={loading}>
              {loading ? "Deleting..." : "Delete Account"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
